/**
 * CSV Export utility for Drift & Accumulation survey responses.
 *
 * Generates a CSV file from survey response data including:
 * - Respondent scope (site, department, team)
 * - All question answers as columns
 * - REPS scores
 * - Timestamps
 */

import { SURVEY_SECTIONS, OE_ELEMENTS } from './constants';

/**
 * Escape a CSV field value.
 * Wraps in double quotes if it contains commas, quotes, or newlines.
 */
function escapeCSV(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Format a Firestore timestamp to a readable date string.
 */
function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toISOString();
}

/**
 * Build all question keys in order from SURVEY_SECTIONS.
 */
function getAllQuestionKeys() {
  const keys = [];
  for (const section of SURVEY_SECTIONS) {
    for (const q of section.questions) {
      keys.push(q.key);
    }
    // Include notes field if present
    if (section.notesKey) {
      keys.push(section.notesKey);
    }
  }
  return keys;
}

/**
 * Build a human-readable label map for question keys.
 */
function getQuestionLabels() {
  const labels = {};
  for (const section of SURVEY_SECTIONS) {
    for (const q of section.questions) {
      labels[q.key] = q.text;
    }
    if (section.notesKey) {
      labels[section.notesKey] = `${section.title} - Notes`;
    }
  }
  return labels;
}

/**
 * Generate CSV content from an array of survey responses.
 *
 * @param {Array} responses - Array of response objects from Firestore
 * @param {Object} survey - The survey object (for title metadata)
 * @returns {string} CSV content as a string
 */
export function generateResponsesCSV(responses, survey) {
  if (!responses || responses.length === 0) {
    return '';
  }

  const questionKeys = getAllQuestionKeys();
  const questionLabels = getQuestionLabels();

  // Build header row
  const headers = [
    'Response ID',
    'Submitted At',
    'Respondent ID',
    'Site',
    'Department',
    'Team',
    // All question columns with readable labels
    ...questionKeys.map((key) => questionLabels[key] || key),
    // REPS scores
    'REPS - Reliability',
    'REPS - Efficiency',
    'REPS - Productivity',
    'REPS - Safety',
  ];

  const rows = [headers.map(escapeCSV).join(',')];

  // Build data rows
  for (const response of responses) {
    const scope = response.respondentScope || {};
    const answers = response.answers || {};
    const reps = response.repsScores || {};

    const row = [
      response.id || '',
      formatTimestamp(response.completedAt),
      response.respondentUid || 'Anonymous',
      scope.siteName || '',
      scope.departmentName || '',
      scope.teamName || '',
      // All question answers
      ...questionKeys.map((key) => {
        const val = answers[key];
        return val !== undefined && val !== null ? val : '';
      }),
      // REPS scores
      reps.reliability !== undefined ? reps.reliability.toFixed(2) : '',
      reps.efficiency !== undefined ? reps.efficiency.toFixed(2) : '',
      reps.productivity !== undefined ? reps.productivity.toFixed(2) : '',
      reps.safety !== undefined ? reps.safety.toFixed(2) : '',
    ];

    rows.push(row.map(escapeCSV).join(','));
  }

  return rows.join('\n');
}

/**
 * Trigger a CSV file download in the browser.
 *
 * @param {string} csvContent - The CSV string content
 * @param {string} filename - The filename for the download
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export survey responses as a CSV file download.
 *
 * @param {Array} responses - Array of response objects
 * @param {Object} survey - The survey object
 */
export function exportSurveyResponsesCSV(responses, survey) {
  const csvContent = generateResponsesCSV(responses, survey);
  if (!csvContent) {
    throw new Error('No responses to export');
  }

  // Build filename from survey title
  const safeName = (survey?.title || 'survey')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);
  const date = new Date().toISOString().split('T')[0];
  const filename = `${safeName}_responses_${date}.csv`;

  downloadCSV(csvContent, filename);
}

/**
 * Generate CSV content from OE snapshots (OE scores summary).
 *
 * Each row is one OE snapshot with:
 * - Scope info (type, id)
 * - Period
 * - Calculated date
 * - REPS scores (R, E, P, S, Overall)
 * - Element breakdown scores (per element per REPS quadrant)
 * - Response count
 * - Warning count
 *
 * @param {Array} snapshots - Array of OE snapshot objects from Firestore
 * @param {Object} organization - The organization object
 * @returns {string} CSV content as a string
 */
export function generateOEScoresCSV(snapshots, organization) {
  if (!snapshots || snapshots.length === 0) {
    return '';
  }

  // Build element breakdown headers
  const elementHeaders = [];
  for (const el of OE_ELEMENTS) {
    elementHeaders.push(`${el.label} - R`);
    elementHeaders.push(`${el.label} - E`);
    elementHeaders.push(`${el.label} - P`);
    elementHeaders.push(`${el.label} - S`);
  }

  const headers = [
    'Snapshot ID',
    'Calculated At',
    'Period',
    'Scope Type',
    'Scope ID',
    'Survey Responses (perception basis)',
    'Approved OE Conditions (score basis)',
    'OE - Overall',
    'OE - Reliability',
    'OE - Efficiency',
    'OE - Productivity',
    'OE - Safety',
    ...elementHeaders,
    'Warnings',
  ];

  const rows = [headers.map(escapeCSV).join(',')];

  for (const snapshot of snapshots) {
    const scores = snapshot.oeScores || {};
    const scope = snapshot.scope || {};
    const elements = snapshot.elementScores || {};
    const warnings = snapshot.warnings || [];

    // Build element breakdown values
    const elementValues = [];
    for (const el of OE_ELEMENTS) {
      const elScores = elements[el.key] || {};
      elementValues.push(formatScore(elScores.R));
      elementValues.push(formatScore(elScores.E));
      elementValues.push(formatScore(elScores.P));
      elementValues.push(formatScore(elScores.S));
    }

    const row = [
      snapshot.id || '',
      formatTimestamp(snapshot.calculatedAt),
      snapshot.period || '',
      scope.type || 'organization',
      scope.id || '',
      snapshot.responseCount !== undefined ? snapshot.responseCount : '',
      conditionsCounted(snapshot),
      formatScore(scores.overall),
      formatScore(scores.reliability),
      formatScore(scores.efficiency),
      formatScore(scores.productivity),
      formatScore(scores.safety),
      ...elementValues,
      warnings.length > 0
        ? warnings.map((w) => `${w.quadrant}: ${w.score}/${w.threshold}`).join('; ')
        : '',
    ];

    rows.push(row.map(escapeCSV).join(','));
  }

  return rows.join('\n');
}

/**
 * Format a numeric score for CSV output.
 */
function formatScore(value) {
  if (value === undefined || value === null) return '';
  return typeof value === 'number' ? value.toFixed(2) : String(value);
}

/**
 * Export OE scores as a CSV file download.
 *
 * @param {Array} snapshots - Array of OE snapshot objects
 * @param {Object} organization - The organization object
 */
export function exportOEScoresCSV(snapshots, organization) {
  const csvContent = generateOEScoresCSV(snapshots, organization);
  if (!csvContent) {
    throw new Error('No OE score data to export');
  }

  const safeName = (organization?.name || 'organization')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);
  const date = new Date().toISOString().split('T')[0];
  const filename = `${safeName}_OE_scores_${date}.csv`;

  downloadCSV(csvContent, filename);
}

/**
 * Generate CSV content for trend data (time series OE scores).
 *
 * Simpler than full OE export — focuses on time series with REPS columns.
 * Optionally filters by date range.
 *
 * @param {Array} snapshots - Array of OE snapshot objects (ordered by calculatedAt desc)
 * @param {Object} options - Optional date range filter { startDate, endDate }
 * @returns {string} CSV content as a string
 */
export function generateTrendCSV(snapshots, options = {}) {
  if (!snapshots || snapshots.length === 0) {
    return '';
  }

  const { startDate, endDate } = options;

  // Filter by date range if provided
  let filtered = [...snapshots];
  if (startDate || endDate) {
    filtered = filtered.filter((snap) => {
      const date = snap.calculatedAt?.toDate
        ? snap.calculatedAt.toDate()
        : new Date(snap.calculatedAt);
      if (startDate && date < new Date(startDate)) return false;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (date > end) return false;
      }
      return true;
    });
  }

  if (filtered.length === 0) return '';

  // Sort oldest first for time series
  filtered.sort((a, b) => {
    const dateA = a.calculatedAt?.toDate ? a.calculatedAt.toDate() : new Date(a.calculatedAt);
    const dateB = b.calculatedAt?.toDate ? b.calculatedAt.toDate() : new Date(b.calculatedAt);
    return dateA - dateB;
  });

  const headers = [
    'Date',
    'Period',
    'Scope Type',
    'Scope ID',
    'Reliability',
    'Efficiency',
    'Productivity',
    'Safety',
    'Overall',
    'Survey Responses (perception basis)',
    'Approved OE Conditions (score basis)',
  ];

  const rows = [headers.map(escapeCSV).join(',')];

  for (const snapshot of filtered) {
    const scores = snapshot.oeScores || {};
    const scope = snapshot.scope || {};

    const row = [
      formatTimestamp(snapshot.calculatedAt),
      snapshot.period || '',
      scope.type || 'organization',
      scope.id || '',
      formatScore(scores.reliability),
      formatScore(scores.efficiency),
      formatScore(scores.productivity),
      formatScore(scores.safety),
      formatScore(scores.overall),
      snapshot.responseCount !== undefined ? snapshot.responseCount : '',
      conditionsCounted(snapshot),
    ];

    rows.push(row.map(escapeCSV).join(','));
  }

  return rows.join('\n');
}

/** Approved + resolution-pending + fading-Resolved OE Conditions behind a snapshot's score (conditions-only scoring, 20 Oct). */
function conditionsCounted(snapshot) {
  const l = snapshot?.conditionLoad;
  if (!l) return '';
  return (Number(l.active) || 0) + (Number(l.resolvedDecaying) || 0);
}

/**
 * Export trend data as a CSV file download.
 *
 * @param {Array} snapshots - Array of OE snapshot objects
 * @param {Object} organization - The organization object
 * @param {Object} options - Optional { startDate, endDate }
 */
export function exportTrendCSV(snapshots, organization, options = {}) {
  const csvContent = generateTrendCSV(snapshots, options);
  if (!csvContent) {
    throw new Error('No trend data to export');
  }

  const safeName = (organization?.name || 'organization')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);
  const date = new Date().toISOString().split('T')[0];
  const filename = `${safeName}_trend_data_${date}.csv`;

  downloadCSV(csvContent, filename);
}
